// ============================================================
// Status chips - what stands between the shot and the body.
//
// defenceChips(target) names what the map cannot draw and you check
// before every roll: force field, cover level/AP, stance, frenzy,
// vehicle facing. Since v8.241.0 they render on the ALWAYS-ON header
// rail (StatusChipView, per-surface testid prefixes); the floating
// StatusBand that duplicated them over the map died in v8.246.0 when
// it started colliding with the target map's sight rings. The turn
// state (the other question a GM asks: is it their go?) renders as
// TurnStrip, a slim row of its own under the map.
// ============================================================
import type { Target, TurnParticipant, TurnTrackerState } from '../types';
import styles from '../styles/StatusBand.module.css';
import { effectiveVehicleFacing } from '../engine/vehicleFacing';

export interface StatusChip {
  key: string;
  icon: string;
  label: string;
  title: string;
  tone?: 'defence' | 'danger' | 'turn';
}

/** The cover level's short word; the rails read it too (plan design-gaps, L7). */
export const COVER_SHORT: Record<string, string> = {
  low: 'LOW',
  half: 'HALF',
  threeQuarter: '¾',
  full: 'FULL',
};

const FACING_ICON: Record<string, string> = {
  front: '▲',
  rear: '▼',
  side: '◀',
};

/** Everything currently protecting or hampering this combatant, in a stable order. */
export function defenceChips(target: Target): StatusChip[] {
  return [forceFieldChip, coverChip, stanceChip, frenzyChip, facingChip]
    .map((chip) => chip(target))
    .filter((c): c is StatusChip => c !== null);
}

function forceFieldChip({ forceField: ff }: Target): StatusChip | null {
  if (!ff?.active || !(ff.protectionRating > 0)) return null;
  const overloads = ff.overloadThreshold > 0;
  return {
    key: 'ff',
    icon: '🛡',
    label: overloads ? `${ff.protectionRating}⚡${ff.overloadThreshold}` : `${ff.protectionRating}`,
    title:
      `Force field ${ff.protectionRating}` +
      (overloads ? ` - overloads on a roll of ${ff.overloadThreshold} or less` : ''),
  };
}

function coverChip({ cover }: Target): StatusChip | null {
  if (!cover || cover.level === 'none' || !((cover.ap || 0) > 0)) return null;
  return {
    key: 'cover',
    icon: '▨',
    label: `${COVER_SHORT[cover.level] || cover.level} ${cover.ap}`,
    title: `Cover: ${cover.level} - ${cover.ap} AP on covered locations`,
  };
}

function stanceChip({ conditions }: Target): StatusChip | null {
  if (!conditions?.some((c) => c === 'Ds' || c.startsWith('Ds'))) return null;
  return {
    key: 'stance',
    icon: '⛨',
    label: 'STANCE',
    title: 'Defensive Stance: attackers take -20 WS; one extra Reaction; no attacks until their next turn',
  };
}

function frenzyChip({ frenzied }: Target): StatusChip | null {
  if (!frenzied) return null;
  return {
    key: 'frenzy',
    icon: '⟲',
    label: 'FRENZY',
    title: 'Frenzied: cannot dodge, parry or retreat',
    tone: 'danger',
  };
}

/**
 * A vehicle's presented facing. WHOSE view this is: two attackers on
 * opposite sides of the same tank see different chips, correctly - saying
 * so here is what stops that reading as a sync bug (v8.52.0).
 */
function facingChip(target: Target): StatusChip | null {
  if (target.bodyType !== 'vehicle') return null;
  const f = effectiveVehicleFacing(target);
  const fromMap = target.vehicleFacing === 'auto' && !!target.vehicleFacingFromMap;
  return {
    key: 'facing',
    icon: FACING_ICON[f] || '▲',
    label: f.toUpperCase(),
    title: fromMap
      ? `Presenting its ${f} to whoever is aiming - the map worked that out from where they are standing, so another attacker may see a different arc`
      : `Presenting its ${f} - that is the armour value a hit uses`,
  };
}

/** "1d10(7) + AB 4 = 11", for the tooltip. */
function initiativeNote(p: TurnParticipant): string {
  if (p.initiative == null) return '';
  if (p.initiativeManual) return `\nInitiative ${p.initiative}, set by hand`;
  if (p.initiativeRoll == null) return `\nInitiative ${p.initiative}`;
  const bonus = p.initiativeBonus ?? 0;
  return `\n1d10(${p.initiativeRoll}) ${bonus < 0 ? '−' : '+'} AB ${Math.abs(bonus)} = ${p.initiative}`;
}

/** Where this combatant stands in the initiative order, if anywhere. */
export function turnChip(tracker: TurnTrackerState | null | undefined, combatantId: string | null): StatusChip | null {
  if (!tracker?.active || !combatantId) return null;
  const index = tracker.participants.findIndex((p) => p.combatantId === combatantId);
  if (index < 0) return null;

  const p = tracker.participants[index];
  const total = tracker.participants.length;
  // The number itself, so a player reads their initiative off their own
  // sheet rather than hunting their chip in a twelve-wide shared bar.
  const init = p.initiative == null ? '' : ` · INIT ${p.initiative}`;

  if (index === tracker.activeIndex && !tracker.paused) {
    return {
      key: 'turn',
      icon: '◆',
      label: `ACTING NOW${init}`,
      title: `It is this combatant’s turn${initiativeNote(p)}`,
      tone: 'turn',
    };
  }
  return {
    key: 'turn',
    icon: '',
    label: `ROUND ${tracker.round} · #${index + 1}/${total}${init}`,
    title: `Position ${index + 1} of ${total} in the initiative order${initiativeNote(p)}`,
  };
}

export interface TurnStripProps {
  tracker?: TurnTrackerState | null;
  combatantId?: string | null;
  /**
   * Reroll this combatant's initiative, from their own sheet.
   *
   * THE PLAYER-FACING ROUTE. Told "everyone reroll", a player should
   * not have to find their chip in a shared bar they may not even be
   * looking at. Omitted (or locked) and no button is drawn.
   */
  onRerollInitiative?: (() => void) | null;
}

/**
 * The turn state as its own slim row (v8.246.0). This USED to be half
 * of a StatusBand floated over the map's letterbox gap; the defence
 * half was a duplicate of the header rail (v8.241.0) and the float
 * collided with the target map's sight rings, so the band died - the
 * chips live in the header, the turn state lives here, and the art
 * gets the whole cell.
 */
export function TurnStrip({ tracker, combatantId, onRerollInitiative }: TurnStripProps) {
  const turn = turnChip(tracker, combatantId ?? null);
  if (!turn) return null;

  return (
    <div className={styles.strip} data-testid="turn-strip">
      <StatusChipView chip={turn} />
      {onRerollInitiative && (
        <button
          type="button"
          className={styles.rerollBtn}
          data-testid="status-reroll-initiative"
          data-ghost-hide="true"
          onClick={onRerollInitiative}
          title="Reroll this combatant’s initiative"
          aria-label="Reroll initiative"
        >
          {'↻'}
        </button>
      )}
    </div>
  );
}

/**
 * One status chip, EXPORTED since v8.241.0 - the identity header and
 * the actor curtain render defence chips too. The prefix keeps every
 * surface's testids distinct: status-band E2E addresses `status-chip-*`
 * UNSCOPED, so a second chip with the same testid anywhere on the page
 * would turn its locators ambiguous.
 */
export function StatusChipView({ chip, testidPrefix = 'status-chip' }: { chip: StatusChip; testidPrefix?: string }) {
  return (
    <span
      className={[styles.chip, chip.tone === 'danger' && styles.danger, chip.tone === 'turn' && styles.turn]
        .filter(Boolean)
        .join(' ')}
      title={chip.title}
      data-testid={`${testidPrefix}-${chip.key}`}
    >
      {chip.icon && <span aria-hidden="true">{chip.icon}</span>}
      {chip.label}
    </span>
  );
}
